/**
 * Default filter values used across the Kanban board
 */
export const DEFAULT_AGE_RANGE: [number, number] = [15, 50];
// GPR is not a 0-100 scale (max observed ~184) and player valuations reach
// ~€200M, so these defaults are intentionally wide enough to never hide a real
// player while keeping comfortable headroom for model drift. The effective
// range shown in the slider is always derived from the board's actual data
// (see `dynamicGprRange`/`dynamicContractValueRange` in MainContainer); these
// hard-coded values are only the "no upper bound" fallback.
export const DEFAULT_GPR_RANGE: [number, number] = [0, 250];
// Contract value is in millions, so 10000 == €10B (~50x the ~€200M real max).
export const DEFAULT_CONTRACT_VALUE_RANGE: [number, number] = [0, 10000];

/**
 * SE-6871: the PERFORMANCE / GOALKEEPER metric scores are 0–100 categorical
 * scores (the same TRANS categorical columns the Watchlist panel filters), so
 * unlike GPR/value their span is fixed and needs no dynamic bounds.
 */
export const DEFAULT_METRIC_SCORE_RANGE: [number, number] = [0, 100];

/**
 * The PERFORMANCE / GOALKEEPER range keys (SE-6871), in render order. One
 * list so the state, the counters, the predicates and the drawer sections
 * can never disagree about which metric dimensions exist.
 */
export const METRIC_RANGE_KEYS = [
  "carryingRange",
  "dribblingRange",
  "passingRange",
  "shootingRange",
  "gkPassingRange",
  "gkShotStoppingRange",
  "gkPositioningRange",
] as const;

/** One of the PERFORMANCE / GOALKEEPER range keys. */
export type MetricRangeKey = (typeof METRIC_RANGE_KEYS)[number];

/**
 * Filter state type definition.
 *
 * SE-6554 dropped `recentActivity` and `countries`. Both were collected by the
 * old filter modal and neither was ever read: `shouldIncludePlayer` had no
 * predicate for either, so every control that set them was inert. They are gone
 * rather than implemented because this pass realigns the filter SURFACE; giving
 * a dead dimension a working predicate is a product change, not a re-skin.
 *
 * `teams` and `tags` survive: both still have live predicates in
 * `utils/playerFilter.ts`. `tags` in particular is stripped downstream by
 * `useSidebarState` (unplaced pool players carry no card tags), so it has no
 * control on this surface — see the follow-ups ticket.
 */
export interface FilterState {
  positions: string[];
  ageRange: [number, number];
  gprRange: [number, number];
  contractValueRange: [number, number];
  teams: string[];
  contractStatus: string[];
  tags: string[];
  /* SE-6871: Watchlist-panel parity — PERFORMANCE and GOALKEEPER ranges. */
  carryingRange: [number, number];
  dribblingRange: [number, number];
  passingRange: [number, number];
  shootingRange: [number, number];
  gkPassingRange: [number, number];
  gkShotStoppingRange: [number, number];
  gkPositioningRange: [number, number];
}

export const DEFAULT_FILTER_STATE: FilterState = {
  positions: [],
  ageRange: DEFAULT_AGE_RANGE,
  gprRange: DEFAULT_GPR_RANGE,
  contractValueRange: DEFAULT_CONTRACT_VALUE_RANGE,
  teams: [],
  contractStatus: [],
  tags: [],
  carryingRange: DEFAULT_METRIC_SCORE_RANGE,
  dribblingRange: DEFAULT_METRIC_SCORE_RANGE,
  passingRange: DEFAULT_METRIC_SCORE_RANGE,
  shootingRange: DEFAULT_METRIC_SCORE_RANGE,
  gkPassingRange: DEFAULT_METRIC_SCORE_RANGE,
  gkShotStoppingRange: DEFAULT_METRIC_SCORE_RANGE,
  gkPositioningRange: DEFAULT_METRIC_SCORE_RANGE,
};

/**
 * Simple filter state type definition (subset of FilterState)
 */
export interface SimpleFilterState {
  positions: string[];
  ageRange: [number, number];
  gprRange: [number, number];
  contractValueRange: [number, number];
  teams: string[];
}

export const DEFAULT_SIMPLE_FILTER_STATE: SimpleFilterState = {
  positions: [],
  ageRange: DEFAULT_AGE_RANGE,
  gprRange: DEFAULT_GPR_RANGE,
  contractValueRange: DEFAULT_CONTRACT_VALUE_RANGE,
  teams: [],
};

/**
 * Helper to ensure filter state always has valid arrays
 * @param filters - Partial or potentially malformed filter state
 * @returns Valid FilterState with all arrays initialized
 */
export const ensureValidFilterState = (
  filters: Partial<FilterState> | unknown
): FilterState => {
  const partialFilters = filters as Partial<FilterState>;
  return {
    positions: Array.isArray(partialFilters?.positions)
      ? partialFilters.positions
      : [],
    ageRange: partialFilters?.ageRange || DEFAULT_AGE_RANGE,
    gprRange: partialFilters?.gprRange || DEFAULT_GPR_RANGE,
    contractValueRange:
      partialFilters?.contractValueRange || DEFAULT_CONTRACT_VALUE_RANGE,
    teams: Array.isArray(partialFilters?.teams) ? partialFilters.teams : [],
    contractStatus: Array.isArray(partialFilters?.contractStatus)
      ? partialFilters.contractStatus
      : [],
    tags: Array.isArray(partialFilters?.tags) ? partialFilters.tags : [],
    carryingRange: partialFilters?.carryingRange || DEFAULT_METRIC_SCORE_RANGE,
    dribblingRange:
      partialFilters?.dribblingRange || DEFAULT_METRIC_SCORE_RANGE,
    passingRange: partialFilters?.passingRange || DEFAULT_METRIC_SCORE_RANGE,
    shootingRange: partialFilters?.shootingRange || DEFAULT_METRIC_SCORE_RANGE,
    gkPassingRange:
      partialFilters?.gkPassingRange || DEFAULT_METRIC_SCORE_RANGE,
    gkShotStoppingRange:
      partialFilters?.gkShotStoppingRange || DEFAULT_METRIC_SCORE_RANGE,
    gkPositioningRange:
      partialFilters?.gkPositioningRange || DEFAULT_METRIC_SCORE_RANGE,
  };
};
